import videojs from 'video.js';
import type Component from 'video.js/dist/types/component';
import type Menu from 'video.js/dist/types/menu/menu';
import type Player from 'video.js/dist/types/player';
import { convertSecondsToTimeCode, createTimecodeString } from './utils';

const CurrentTimeDisplay = videojs.getComponent('CurrentTimeDisplay');
const DurationDisplay = videojs.getComponent('DurationDisplay');
const TimeTooltip = videojs.getComponent('TimeTooltip');
const PlaybackRateMenuButton = videojs.getComponent('PlaybackRateMenuButton');
const MenuComponent = videojs.getComponent('Menu');
const ComponentClass = videojs.getComponent('Component');

interface PlaybackRateMenuItem {
  el_: HTMLElement;
  rate: number;
  selected: () => void;
  handleClick: () => void;
  on: () => void;
}

interface ExtendedPlayer extends Player {
  frameRate: number;
  df: boolean;
  inpoint: string;
  finalDelimiter: string;
  currentPlaybackRate?: number;
  changePlaybackRate?: (rate: number) => void;
}

// Define the constructor type for extending runtime components
// We use a simplified base type to avoid conflicts with MenuButton's type definitions
interface MenuButtonInstance {
  player_: ExtendedPlayer;
  labelEl_: HTMLElement;
  items: PlaybackRateMenuItem[];
  options_: { title?: string };
  hideThreshold_: number;
  createItems(): unknown[];
}

export class CustomPlaybackRateMenuButton extends (PlaybackRateMenuButton as unknown as {
  new (player: Player, opts?: Record<string, unknown>): MenuButtonInstance;
}) {
  private currentInterval?: ReturnType<typeof setInterval>;
  player_!: ExtendedPlayer;
  labelEl_!: HTMLElement;
  items!: PlaybackRateMenuItem[];
  options_!: { title?: string };
  hideThreshold_!: number;

  constructor(player: Player, opts?: Record<string, unknown>) {
    super(player, opts);
    this.changePlaybackRate = this.changePlaybackRate.bind(this);
    this.changeActiveMenu = this.changeActiveMenu.bind(this);
    // Defer setting changePlaybackRate on player until player_ is available
    const extPlayer = player as ExtendedPlayer;
    extPlayer.changePlaybackRate = this.changePlaybackRate;
  }

  updateLabel(): void {}

  changePlaybackRate(rate: number): void {
    const item = this.items.find(({ rate: _rate }) => _rate === rate);
    if (item) {
      this.changeActiveMenu(item);
    }

    this.labelEl_.textContent = `${rate}x`;

    this.player_.currentPlaybackRate = rate;
    clearInterval(this.currentInterval);
    if (rate >= 0) {
      this.player_.playbackRate(rate);
    } else {
      this.player_.playbackRate(0);
      this.currentInterval = setInterval(
        () => {
          if (this.player_.paused()) {
            return;
          }
          if (typeof this.player_.currentTime !== 'function') {
            return;
          }
          const _currTime = this.player_.currentTime() ?? 0;
          if (_currTime === 0) {
            clearInterval(this.currentInterval);
          }
          if (this.player_.readyState() === 4) this.player_.currentTime(_currTime + -0.1);
        },
        1000 / this.player_.frameRate / Math.abs(rate),
      );
    }
  }

  changeActiveMenu(item: PlaybackRateMenuItem): void {
    this.items.forEach(({ el_ }) => {
      el_.classList.remove('vjs-selected');
      el_.setAttribute('aria-checked', 'false');
    });
    item.el_.classList.add('vjs-selected');
    item.el_.setAttribute('aria-checked', 'true');
  }

  handleClick(): void {}

  createMenu(): Menu {
    const menu = new (MenuComponent as unknown as { new (player: Player, opts?: Record<string, unknown>): Menu })(
      this.player_,
      { menuButton: this } as Record<string, unknown>,
    );

    this.hideThreshold_ = 0;

    if (this.options_.title) {
      const titleEl = videojs.dom.createEl('li', {
        className: 'vjs-menu-title',
        textContent: `${this.options_.title}x`,
        tabIndex: -1,
      });

      const titleComponent = new (
        ComponentClass as unknown as { new (player: Player, opts?: Record<string, unknown>): Component }
      )(this.player_, { el: titleEl } as Record<string, unknown>);

      menu.addItem(titleComponent as unknown as Component);
    }

    this.items = this.createItems() as unknown as PlaybackRateMenuItem[];

    if (this.items) {
      for (let i = 0; i < this.items.length; i++) {
        this.items[i].selected = () => {};
        this.items[i].handleClick = () => {
          this.items[i].on = () => {};
          this.changePlaybackRate(this.items[i].rate);
        };

        menu.addItem(this.items[i] as unknown as Component);
      }
    }

    return menu;
  }

  createItems(): unknown[] {
    return [];
  }
}

export class CustomTimeTooltip extends (TimeTooltip as unknown as {
  new (player: Player, opts?: Record<string, unknown>): Component;
}) {
  player_!: ExtendedPlayer;
  el_!: HTMLElement;

  updateTime(seekBarRect: DOMRect, seekBarPoint: number, time: number, cb?: () => void): void {
    this.requestNamedAnimationFrame('TimeTooltip#updateTime', () => {
      this.update(seekBarRect, seekBarPoint, time);
      if (cb) {
        cb();
      }
    });
  }

  update(seekBarRect: DOMRect, seekBarPoint: number, b: number): void {
    const tooltipRect = videojs.dom.findPosition(this.el_);
    const playerRect = videojs.dom.getBoundingClientRect(this.player_.el());

    const seekBarPointPx = seekBarRect.width * seekBarPoint;

    if (!playerRect || !tooltipRect) {
      return;
    }
    const spaceLeftOfPoint = seekBarRect.left - playerRect.left + seekBarPointPx;
    const spaceRightOfPoint = seekBarRect.width - seekBarPointPx + (playerRect.right - seekBarRect.right);
    let pullTooltipBy = tooltipRect.width / 2;
    if (spaceLeftOfPoint < pullTooltipBy) {
      pullTooltipBy += pullTooltipBy - spaceLeftOfPoint;
    } else if (spaceRightOfPoint < pullTooltipBy) {
      pullTooltipBy = spaceRightOfPoint;
    }
    if (pullTooltipBy < 0) {
      pullTooltipBy = 0;
    } else if (pullTooltipBy > tooltipRect.width) {
      pullTooltipBy = tooltipRect.width;
    }
    pullTooltipBy = Math.round(pullTooltipBy);
    this.el_.style.right = `-${pullTooltipBy}px`;

    const { frameRate, df, inpoint } = this.player_;
    this.write(convertSecondsToTimeCode({ secs: b, frameRate, inpoint, df }));
  }

  write(content: string): void {
    this.el_.textContent = content;
  }

  requestNamedAnimationFrame(name: string, cb: () => void): string {
    requestAnimationFrame(cb);
    return name;
  }
}

export class CustomDurationDisplay extends (DurationDisplay as unknown as {
  new (player: Player, opts?: Record<string, unknown>): Component;
}) {
  player_!: ExtendedPlayer;
  el_!: HTMLElement;
  contentEl_!: HTMLElement;
  textNode_?: Text;

  constructor(player: Player, opts?: Record<string, unknown>) {
    super(player, opts);
    this.el_ = this.createEl(
      'div',
      {
        className: 'vjs-share-bar video-share share-buttons',
      },
      {
        role: 'group',
      },
    ) as HTMLElement;
  }

  updateTextNode_(seconds?: number): string | undefined {
    if (!this.player_.inpoint) {
      return;
    }
    if (!seconds) {
      return;
    }

    let oldNode = this.textNode_;
    if (this.textNode_ !== undefined) {
      return;
    }
    const { frameRate, df, inpoint, finalDelimiter } = this.player_;
    const timecodeStr = createTimecodeString({
      seconds,
      frameRate,
      df,
      inpoint,
      finalDelimiter,
    });
    this.textNode_ = document.createTextNode(timecodeStr);
    if (oldNode && this.contentEl_.firstChild !== oldNode) {
      oldNode = undefined;
    }
    if (oldNode) {
      this.contentEl_.replaceChild(this.textNode_, oldNode);
    } else {
      this.contentEl_.appendChild(this.textNode_);
    }
    return 'vas';
  }

  createEl(type: string, props: Record<string, unknown>, attrs: Record<string, unknown>): Element {
    return videojs.dom.createEl(type, props, attrs);
  }
}

export class CustomCurrentTimeDisplay extends (CurrentTimeDisplay as unknown as {
  new (player: Player, opts?: Record<string, unknown>): Component;
}) {
  player_!: ExtendedPlayer;
  el_!: HTMLElement;
  contentEl_!: HTMLElement;
  textNode_?: Text;

  constructor(player: Player, opts?: Record<string, unknown>) {
    super(player, opts);
    this.el_ = this.createEl(
      'div',
      {
        className: 'vjs-share-bar video-share share-buttons',
      },
      {
        role: 'group',
      },
    ) as HTMLElement;
  }

  updateTextNode_(seconds?: number): void {
    if (!this.player_.inpoint) {
      return;
    }
    let oldNode = this.textNode_;

    const { frameRate, df, inpoint, finalDelimiter } = this.player_;
    const timecodeStr = createTimecodeString({
      seconds: seconds || 0,
      frameRate,
      df,
      inpoint,
      finalDelimiter,
    });

    this.textNode_ = document.createTextNode(timecodeStr);

    if (oldNode && this.contentEl_.firstChild !== oldNode) {
      oldNode = undefined;
    }

    if (oldNode) {
      this.contentEl_.replaceChild(this.textNode_, oldNode);
    } else {
      this.contentEl_.appendChild(this.textNode_);
    }
  }

  createEl(type: string, props: Record<string, unknown>, attrs: Record<string, unknown>): Element {
    return videojs.dom.createEl(type, props, attrs);
  }
}
